import React, { useState } from 'react';
import { Dropdown, Form, Input, Layout, Modal, Popconfirm, Select, Tag, Tooltip } from 'antd';
import { User_Sort_Field, User_type } from "../../../constant/constant";
import UserCreate from '../user-create/user-create';
import Grid from '../../../common/table';
import { EditableCellProps, IUser, IUserDataTable } from "../user-interfaces";
import resources from "../../../resources";
import server from "../../../server";
import dayjs from "dayjs";
import Locale from "../../../core/locale/locale";
import { CircleX, Ellipsis, Eye, Mail, Pencil } from "lucide-react";
import '../../../styles/component/user/userDataTable.scss';
import "../../../styles/component/user/userCreate.scss";
import { useNavigate } from "react-router-dom";

const userTypesEnum = Object.keys(User_type);

const EditableCell: React.FC<EditableCellProps> = (
    {
        editing,
        dataIndex,
        title,
        inputType,
        record,
        index,
        children,
        ...restProps
    }
) => {
    const inputNode = dataIndex === 'roles' ?
        <Select mode="multiple"
                placeholder="Select the role"
                style={{flex: 1}}
        >
            {userTypesEnum.map((userType, index: number) =>
                <Select.Option key={index} value={userType.toString().toUpperCase()}>
                    {userType.toString().toUpperCase()}
                </Select.Option>)
            }
        </Select> : dataIndex === 'status' ?
            <Select
                placeholder="Choose status"
                style={{flex: 1}}
                options={[
                    {
                        value: 'ACTIVE',
                        label: 'Active',
                    },
                    {
                        value: 'IN_ACTIVE',
                        label: 'In Active',
                    }]}
            >
            </Select> : <Input/>;
    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{margin: 0}}
                    rules={[
                        {
                            required: true,
                            message: `Please Input ${title}!`,
                        }
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const UserDataTable = (props: IUserDataTable) => {
        const [form] = Form.useForm();
        const editingKey = '';
        const [isUserModalVisible, setIsUserModalVisible] = useState(false);
        const [isEditModalVisible, setIsEditModalVisible] = useState(false);
        const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
        const tempResource = resources.userDataTable
        const navigate = useNavigate();

        const handelGotoEmployeeCreate = (record: IUser) => {
            navigate('/employee/create/personal', {state: {record: record}})
        }

        const showUserModal = (record: IUser) => {
            setSelectedUser(record);
            setIsUserModalVisible(true);
        };

        const closeModal = () => {
            setIsEditModalVisible(false);
            setIsUserModalVisible(false)
            setSelectedUser(null);
        };

        const isEditing = (record: IUser) => record.id === editingKey;

        const edit = (record: IUser) => {
            setSelectedUser(record);
            form.setFieldsValue({...record});
            setIsEditModalVisible(true);
        }

        const markUserInActive = async (record: any) => {
            await server.user.markInactive(record.id);
            const userData = props.userData.filter((user: any) => (record.id !== user.id))
            props.setUserData(userData);
        }

        const showColumnsAccordingToScreen = () => {
            return props.userListType === 'user';
        }

        const sendCredentialEmail = async (userId: string) => {
            await server.user.sendCredentialEmail(userId)
        }

        const columns = [
            {
                title: 'Name',
                dataIndex: 'name',
                width: '18%',
                editable: true,
                sorter: true,
                sortField: User_Sort_Field.firstName,
                show: true,
                sortOrder:
                    props.sortField === User_Sort_Field.firstName
                        ? props.sortDirection === "ASC"
                        ? "ascend"
                        : "descend"
                        : null,

            },
            {
                title: 'Email',
                dataIndex: 'email',
                width: '18%',
                show: true,
                sorter: true,
                sortField: User_Sort_Field.email,
                sortOrder:
                    props.sortField === User_Sort_Field.email
                        ? props.sortDirection === "ASC"
                        ? "ascend"
                        : "descend"
                        : null,

            },
            {
                title: 'Status',
                dataIndex: 'status',
                width: '10%',
                editable: false,
                show: true,
                render: (status: any, record: any) => (
                    <Tag
                        key={record.id}
                        className={`status-tag ${status.toLowerCase() === 'active' ? 'active' : 'inactive'}`}
                    >
                        {status.toUpperCase()}
                    </Tag>

                ),
            },
            {
                title: 'Role',
                dataIndex: 'roles',
                editable: true,
                width: '10%',
                show: true,
                render: (_: any, record: any) => (
                    <>
                        {record.roles?.map((tag: string, index: number) => {
                            const isInactive = !props.isActiveUsers;
                            const isEmployee = tag === 'EMPLOYEE' && record.employeeId;
                            const key = `${record.id}-${tag}`;
                            if (isInactive) {
                                return (
                                    <Tag
                                        key={key}
                                        className={`user-tag ${tag.toLowerCase()} inactive`}
                                    >
                                        {tag.toUpperCase()}
                                    </Tag>
                                );
                            }

                            return (
                                !isEmployee ? <Popconfirm
                                        key={key}
                                        title={"Create Employee Profile?"}
                                        description={"The user account has been created successfully. Would you like to create the employee profile now?"}
                                        trigger={"hover"}
                                        okText={'Yes'}
                                        onConfirm={() => handelGotoEmployeeCreate(record)}>
                                        <Tag
                                            key={key}
                                            className={`user-tag ${isEmployee ? 'employee active' : 'inactive'}`}
                                        >
                                            {tag.toUpperCase()}
                                        </Tag>
                                    </Popconfirm> :
                                    <Tag
                                        key={key}
                                        className={`user-tag ${isEmployee ? 'employee active' : 'inactive'}`}
                                    >
                                        {tag.toUpperCase()}
                                    </Tag>
                            );
                        })}
                    </>
                ),
            },
            {
                title: 'Created On',
                key: 'createdOn',
                sorter: true,
                sortField: User_Sort_Field.createdOn,
                sortOrder: props.sortField === User_Sort_Field.createdOn
                    ? props.sortDirection === "ASC"
                        ? "ascend"
                        : props.sortDirection === "DESC"
                            ? "descend"
                            : null
                    : null
                ,
                width: '12%',
                show: true,
                render: (_: any, record: any) =>
                    record.auditInfo?.createdOn
                        ? dayjs(record.auditInfo?.createdOn).format(Locale.displayDate)
                        : '-',
            },
            {
                title: 'Created By',
                key: 'createdBy',
                width: '15%',
                show: true,
                render: (_: any, record: any) =>
                    record.auditInfo?.createdBy || '-',
            },
            {
                title: 'Actions',
                dataIndex: 'operation',
                width: '5%',
                show: showColumnsAccordingToScreen(),
                render: (_: any, record: IUser) => {
                    const showMail = !record.roles?.includes("ADMIN") && props.isActiveUsers && !!record.employeeId;
                    const menuItems = [
                        {
                            label: 'View',
                            key: 0,
                            icon: <Eye className={'action-view'}/>,
                            onClick: () => showUserModal(record)
                        },
                        ...(props.isActiveUsers
                            ? [
                                {
                                    label: "Edit",
                                    key: "edit",
                                    icon: (
                                        <Pencil
                                            className={`action-edit ${
                                                editingKey !== "" ? "action-disabled" : ""
                                            }`}
                                        />
                                    ),
                                    onClick: () => edit(record),
                                },
                                {
                                    label: "Deactivate",
                                    key: "deactivate",
                                    icon: <CircleX className="action-deactivate"/>,
                                    onClick: () =>
                                        Modal.confirm({
                                            title: "Are you sure you want to deactivate this user?",
                                            content:
                                                "If you deactivate the user, its corresponding employee will also be deactivated.",
                                            okText: "Yes",
                                            onOk: () => markUserInActive(record),
                                        }),
                                },
                                {
                                    label: 'Send Mail',
                                    key: 0,
                                    icon: <Mail
                                        className={`action-edit action-mail ${showMail ? 'action-disabled' : ''}`}
                                    />,
                                    disabled: showMail,
                                    onClick: () => sendCredentialEmail(record.id)
                                },
                            ]
                            : []),
                    ]

                    return (
                        <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                            <Ellipsis className={'pn-table-actions-icon'}/>
                        </Dropdown>
                    )
                },
            },
        ];

        const mergedColumns = columns.map((col) => {
            if (!col.editable) {
                return col;
            }
            return {
                ...col,
                onCell: (record: IUser) => ({
                    record,
                    inputType: col.dataIndex === 'role' ? 'number' : 'text',
                    dataIndex: col.dataIndex,
                    title: col.title,
                    editing: isEditing(record),
                }),
            };
        });

        const components = {
            body: {
                cell: EditableCell,
            },
        }

        function handleCreateModalClose() {
            props.setOpenCreateUserModal(false);
        }

        return (
            <Layout className="with-background">

                <div className='data-table user-data-table'>
                    <Grid
                        components={components}
                        rowKey={'id'}
                        bordered={true}
                        dataSource={props.userData}
                        columns={mergedColumns}
                        rowClassName={'editable-row'}
                        pagination={false}
                        paginationButton={true}
                        handlePrevPage={props.handlePrevPage}
                        pageSize={props.pageSize}
                        onChange={(pagination: any, filters: any, sorter: any) => {
                            let direction: "ASC" | "DESC" | undefined;

                            switch (sorter.order) {
                                case "ascend":
                                    direction = "ASC";
                                    break;
                                case "descend":
                                    direction = "DESC";
                                    break;
                                default:
                                    direction = undefined;
                            }
                            const backendField = sorter.column?.sortField ?? User_Sort_Field.createdOn;

                            if (!backendField) return;

                            props.handleSortChange(backendField, direction);
                        }}
                        handleNextPage={props.handleNextPage}
                        pageNumber={props.pageNumber}
                    />
                </div>

                <Modal
                    title={"Create New User"}
                    className="hrms-app user-create-modal"
                    open={props.openCreateUserModal}
                    onCancel={handleCreateModalClose}
                    footer={null}>
                    <UserCreate mode={"create"} userData={props.userData} setUserData={props.setUserData}/>
                </Modal>
                <Modal
                    title={tempResource.userDetailModalTitle}
                    className="hrms-app user-detail-modal"
                    open={isUserModalVisible}
                    onCancel={closeModal}
                    footer={null}
                    centered
                >
                    <UserCreate
                        initialData={selectedUser}
                        mode="view"
                    />
                </Modal>

                <Modal
                    title={tempResource.userEditModalTitle}
                    open={isEditModalVisible}
                    onCancel={closeModal}
                    className="hrms-app edit-user-modal"
                    footer={null}
                >
                    <UserCreate
                        initialData={selectedUser}
                        mode="edit"
                        onModalClose={closeModal}
                        userData={props.userData}
                        setUserData={props.setUserData}
                    />
                </Modal>
            </Layout>
        );
    }
;

export default UserDataTable;
